(() => {
  'use strict';

  const SELECTORS = {
    openBtn: '[data-mini-cart-gift-open]',
    modal: '[data-mini-cart-gift-modal]',
    close: '[data-mini-cart-gift-close]',
    add: '[data-mini-cart-gift-add]',
    threshold: '[data-cart-threshold]',
    progress: '[data-gift-progress]',
    regionalIntro: '[data-free-gift-intro-modal]',
    regionalPicker: '[data-free-gift-picker-modal]',
    regionalOpenPicker: '[data-free-gift-open-picker]',
    regionalIntroAdd: '[data-free-gift-intro-add]',
    regionalIntroPass: '[data-free-gift-intro-pass]',
    regionalIntroClose: '[data-free-gift-intro-close]',
    regionalPickerClose: '[data-free-gift-picker-close]',
    regionalClose: '[data-free-gift-intro-close], [data-free-gift-picker-close]',
    regionalOverlay: '[data-free-gift-intro-overlay], [data-free-gift-picker-overlay]',
    regionalItemAdd: '[data-free-gift-item-add]',
    regionalVariant: '[data-free-gift-variant-select]',
    offersTab: '[data-cart-drawer-tab]',
    offersShowProducts: '[data-cart-drawer-offers-show-products]',
    offersRoot: '[data-cart-drawer-offers]',
    offersShipping: '[data-cart-drawer-offer-shipping]',
    offersGift: '[data-cart-drawer-offer-gift]',
    offersShippingAmount: '[data-cart-drawer-offer-shipping-amount]',
    offersGiftAmount: '[data-cart-drawer-offer-gift-amount]',
    offersShippingStatus: '[data-cart-drawer-offer-shipping-status]',
    offersGiftStatus: '[data-cart-drawer-offer-gift-status]',
  };

  const SESSION_DISMISS = 'jazz_gift_popup_dismissed';
  const GIFT_MODAL_ANIM_MS = 420;

  let lastCartTotalCents = -1;
  let pendingOpenCart = false;
  let giftAddQueue = Promise.resolve();
  let giftAddInFlight = 0;
  let invalidGiftRemovalInFlight = false;
  let giftCartChangeGuardPassThrough = false;

  function getCartDrawer() {
    return document.getElementById('CartDrawer');
  }

  /** SG + US + CA: mirrored from Liquid via #CartDrawer[data-jazz-free-gift-regions] */
  function isFreeGiftRegion() {
    const d = getCartDrawer();
    return !!(d && d.hasAttribute('data-jazz-free-gift-regions'));
  }

  function isRegionalFreeGiftFlow() {
    const d = getCartDrawer();
    return !!(d && d.hasAttribute('data-free-gift-regional-flow'));
  }

  function hasOffersTabs() {
    const d = getCartDrawer();
    return !!(d && d.hasAttribute('data-cart-drawer-offers-tabs'));
  }

  function getThresholdBlock(drawer) {
    const root = drawer || getCartDrawer();
    if (!root) return null;
    return root.querySelector('[data-cart-drawer-panel="cart"] ' + SELECTORS.threshold) || root.querySelector(SELECTORS.threshold);
  }

  function setCartDrawerTab(tabName) {
    const drawer = getCartDrawer();
    if (!drawer || !hasOffersTabs()) return;
    drawer.querySelectorAll('[data-cart-drawer-panel]').forEach((panel) => {
      const active = panel.getAttribute('data-cart-drawer-panel') === tabName;
      if (active) {
        panel.removeAttribute('hidden');
        panel.classList.add('is-active');
      } else {
        panel.hidden = true;
        panel.classList.remove('is-active');
      }
    });
    drawer.querySelectorAll(SELECTORS.offersTab).forEach((tab) => {
      const active = tab.getAttribute('data-cart-drawer-tab') === tabName;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  }

  function getOffersConfig(drawer) {
    const root = drawer && drawer.querySelector(SELECTORS.offersRoot);
    const block = getThresholdBlock(drawer);
    if (!root) return null;
    return {
      root: root,
      shipThreshold:
        Number(root.getAttribute('data-ship-threshold-cents')) ||
        Number(block && block.getAttribute('data-ship-threshold-cents')) ||
        0,
      giftThreshold:
        Number(root.getAttribute('data-gift-threshold-cents')) ||
        Number(block && block.getAttribute('data-gift-threshold-cents')) ||
        0,
      textShipBefore:
        root.getAttribute('data-offers-text-ship-before') ||
        'Add [[amount]] more to get Free Shipping on this order',
      textShipUnlocked:
        root.getAttribute('data-offers-text-ship-unlocked') ||
        'Congratulations, You have unlocked Free Shipping on this order.',
      textShipEmpty:
        root.getAttribute('data-offers-text-ship-empty') ||
        'Add items worth [[amount]] to get free shipping',
      textGiftBefore:
        root.getAttribute('data-offers-text-gift-before') ||
        'Add [[amount]] more to get your free gift on this order',
      textGiftAfterShip:
        root.getAttribute('data-offers-text-gift-after-ship') ||
        "You've unlocked free shipping! You're only [[amount]] away from receiving a FREE GIFT",
      textGiftUnlocked:
        root.getAttribute('data-offers-text-gift-unlocked') ||
        "Congratulations! You've unlocked free shipping and free gift with your order.",
      textGiftEmpty:
        root.getAttribute('data-offers-text-gift-empty') ||
        'Add items worth [[amount]] to get a free gift',
    };
  }

  function syncRegionalGiftAddButtons(cart) {
    const drawer = getCartDrawer();
    if (!drawer || !isRegionalFreeGiftFlow()) return;

    const block = getThresholdBlock(drawer);
    const config = getOffersConfig(drawer);
    let giftThreshold =
      (config && config.giftThreshold) ||
      Number(block && block.getAttribute('data-gift-threshold-cents')) ||
      0;
    const shipThreshold =
      (config && config.shipThreshold) ||
      Number(block && block.getAttribute('data-ship-threshold-cents')) ||
      0;
    if (giftThreshold < shipThreshold) giftThreshold = shipThreshold;

    const totalCents = Number(cart && cart.total_price) || 0;
    const itemCount = Number(cart && cart.item_count) || 0;
    const cartIsEmpty = itemCount === 0;
    const giftUnlocked = !cartIsEmpty && giftThreshold > 0 && totalCents >= giftThreshold;
    const hasFreeGift = cartHasFreeGift(cart);
    const limitOne = !block || block.getAttribute('data-limit-one-gift') !== 'false';
    const canAddGift = giftUnlocked && !(hasFreeGift && limitOne);

    drawer.querySelectorAll(SELECTORS.regionalItemAdd).forEach((btn) => {
      if (!canAddGift) {
        btn.hidden = true;
        btn.disabled = true;
      } else {
        btn.hidden = false;
        if (giftAddInFlight === 0) {
          resetGiftAddButton(btn);
        } else {
          btn.disabled = true;
        }
      }
    });
  }

  function updateOffersPanel(cart) {
    if (!hasOffersTabs()) return;
    const drawer = getCartDrawer();
    if (!drawer) return;

    const config = getOffersConfig(drawer);
    if (!config) return;

    const shipAmountEl = drawer.querySelector(SELECTORS.offersShippingAmount);
    const giftAmountEl = drawer.querySelector(SELECTORS.offersGiftAmount);
    const shipStatusEl = drawer.querySelector(SELECTORS.offersShippingStatus);
    const giftStatusEl = drawer.querySelector(SELECTORS.offersGiftStatus);
    const shipItem = drawer.querySelector(SELECTORS.offersShipping);
    const giftItem = drawer.querySelector(SELECTORS.offersGift);

    const totalCents = Number(cart && cart.total_price) || 0;
    const itemCount = Number(cart && cart.item_count) || 0;
    const cartIsEmpty = itemCount === 0;
    let shipThreshold = config.shipThreshold;
    let giftThreshold = config.giftThreshold;
    if (giftThreshold < shipThreshold) giftThreshold = shipThreshold;
    const hasFreeGift = cartHasFreeGift(cart);

    if (shipAmountEl && shipThreshold > 0) shipAmountEl.textContent = formatMoney(shipThreshold);
    if (giftAmountEl && giftThreshold > 0) giftAmountEl.textContent = formatMoney(giftThreshold);

    const shipUnlocked = !cartIsEmpty && shipThreshold > 0 && totalCents >= shipThreshold;
    const giftUnlocked = !cartIsEmpty && giftThreshold > 0 && totalCents >= giftThreshold;

    if (shipItem) shipItem.classList.toggle('is-unlocked', shipUnlocked);
    if (giftItem) giftItem.classList.toggle('is-unlocked', giftUnlocked || hasFreeGift);

    if (shipStatusEl) {
      if (cartIsEmpty && shipThreshold > 0) {
        shipStatusEl.innerHTML = withAmount(config.textShipEmpty, formatMoney(shipThreshold));
      } else if (shipUnlocked) {
        shipStatusEl.textContent = config.textShipUnlocked;
      } else if (shipThreshold > 0) {
        const remainingToShip = Math.max(0, shipThreshold - totalCents);
        shipStatusEl.innerHTML = withAmount(config.textShipBefore, formatMoney(remainingToShip));
      } else {
        shipStatusEl.textContent = '';
      }
    }

    if (giftStatusEl) {
      if (hasFreeGift) {
        giftStatusEl.textContent = 'Your free gift has been added to this order.';
      } else if (cartIsEmpty && giftThreshold > 0) {
        giftStatusEl.innerHTML = withAmount(config.textGiftEmpty, formatMoney(giftThreshold));
      } else if (giftUnlocked) {
        giftStatusEl.textContent = config.textGiftUnlocked;
      } else if (shipUnlocked && giftThreshold > 0) {
        const remainingToGift = Math.max(0, giftThreshold - totalCents);
        giftStatusEl.innerHTML = withAmount(config.textGiftAfterShip, formatMoney(remainingToGift));
      } else if (giftThreshold > 0) {
        const remainingToGift = Math.max(0, giftThreshold - totalCents);
        giftStatusEl.innerHTML = withAmount(config.textGiftBefore, formatMoney(remainingToGift));
      } else {
        giftStatusEl.textContent = '';
      }
    }

    syncRegionalGiftAddButtons(cart);
  }

  function formatMoney(cents) {
    if (typeof window.MiniCartFormatMoney === 'function') {
      return window.MiniCartFormatMoney(cents);
    }
    return '$' + Math.round(Number(cents || 0) / 100);
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function withAmount(template, amountMoney) {
    const amountHtml =
      '<strong class="cart-threshold__amount">' + escapeHtml(amountMoney) + '</strong>';
    return String(template || '')
      .replace(/\{\{\s*amount\s*\}\}/gi, amountHtml)
      .replace(/\[\[amount\]\]/gi, amountHtml);
  }

  function computeFillPct(totalCents, shipThreshold, giftThreshold) {
    let fillPct = 0;
    if (shipThreshold > 0 && giftThreshold > shipThreshold) {
      if (totalCents < shipThreshold) {
        fillPct = (totalCents * 50) / shipThreshold;
      } else if (totalCents < giftThreshold) {
        const remainder = giftThreshold - shipThreshold;
        const progressed = totalCents - shipThreshold;
        fillPct = (progressed * 50) / remainder + 50;
      } else {
        fillPct = 100;
      }
    } else if (shipThreshold > 0) {
      fillPct = totalCents >= shipThreshold ? 100 : (totalCents * 100) / shipThreshold;
    }
    fillPct = Math.max(0, Math.min(100, fillPct));
    return Math.round(fillPct * 100) / 100;
  }

  /** Product handles used as complimentary free gifts (must stay in sync with cart-drawer.liquid). */
  const FREE_GIFT_HANDLES = new Set([
    'straw-gift-set',
    'cake-knife-gift-set',
    'straw-set-free-gift',
  ]);

  const GIFT_ONLY_CHECKOUT_MESSAGE =
    'Free gifts can’t be checked out alone. Add another item to continue.';

  function cartHasFreeGift(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;
    return getFreeGiftLines(cart).length > 0;
  }

  function isFreeGiftLine(line) {
    if (!line) return false;
    if (line.properties && String(line.properties._jazz_free_gift) === 'true') return true;
    const handle = String(line.handle || line.product_handle || '').toLowerCase();
    return FREE_GIFT_HANDLES.has(handle);
  }

  function getFreeGiftLines(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];
    return cart.items.filter(isFreeGiftLine);
  }

  function getGiftEligibleTotal(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    return cart.items.reduce((sum, line) => {
      if (isFreeGiftLine(line)) return sum;
      return sum + (Number(line.final_line_price) || 0);
    }, 0);
  }

  /** True when the cart has items but no paid merchandise (gift-only / $0 merchandise). */
  function isGiftOnlyCart(cart) {
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return false;
    if (getGiftEligibleTotal(cart) > 0) return false;
    return true;
  }

  function shouldBlockCheckout(cart) {
    if (!cart || !cart.item_count) return true;
    if (isGiftOnlyCart(cart)) return true;
    if ((Number(cart.total_price) || 0) <= 0) return true;
    return false;
  }

  function getGiftThresholdFromBlocks(blocks) {
    if (!blocks || !blocks.length) return 0;
    for (const block of blocks) {
      if (!block) continue;
      const shipThreshold = Number(block.getAttribute('data-ship-threshold-cents')) || 0;
      let giftThreshold = Number(block.getAttribute('data-gift-threshold-cents')) || 0;
      if (giftThreshold < shipThreshold) giftThreshold = shipThreshold;
      if (giftThreshold > 0) return giftThreshold;
    }
    return 0;
  }

  /** US/CA $300, SG $250 — mirrors sections/cart-drawer.liquid when DOM blocks are missing. */
  function getFallbackGiftThresholdCents() {
    const market = String(
      (window.auroraTheme && window.auroraTheme.marketHandle) ||
        (window.Shopify && window.Shopify.country) ||
        ''
    ).toLowerCase();
    if (market === 'sg' || market === 'singapore') return 25000;
    if (
      market === 'us' ||
      market === 'ca' ||
      market === 'united-states' ||
      market === 'canada'
    ) {
      return 30000;
    }
    return 30000;
  }

  function getEffectiveGiftThresholdCents(blocks) {
    const fromBlocks = getGiftThresholdFromBlocks(blocks);
    if (fromBlocks > 0) return fromBlocks;
    const drawer = getCartDrawer();
    if (drawer) {
      const fromDrawer = Number(drawer.getAttribute('data-gift-threshold-cents')) || 0;
      if (fromDrawer > 0) return fromDrawer;
    }
    return getFallbackGiftThresholdCents();
  }

  function syncCheckoutButtons(cart) {
    const blocked = shouldBlockCheckout(cart);
    document.querySelectorAll('[data-cart-checkout-button]').forEach((btn) => {
      if (blocked) {
        btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('href', '#');
        btn.classList.add('is-disabled');
        btn.setAttribute('title', GIFT_ONLY_CHECKOUT_MESSAGE);
      } else {
        btn.removeAttribute('aria-disabled');
        btn.classList.remove('is-disabled');
        btn.removeAttribute('title');
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        btn.setAttribute('href', root.replace(/\/?$/, '/') + 'checkout');
      }
    });
    document.querySelectorAll('[data-cart-page-checkout-button]').forEach((btn) => {
      btn.disabled = blocked;
      if (blocked) {
        btn.setAttribute('title', GIFT_ONLY_CHECKOUT_MESSAGE);
      } else {
        btn.removeAttribute('title');
      }
    });
    document.querySelectorAll('.dynamic-checkout-buttons').forEach((el) => {
      el.hidden = blocked;
      el.style.display = blocked ? 'none' : '';
    });
    document.querySelectorAll('[data-gift-only-checkout-note]').forEach((el) => {
      el.hidden = !blocked || !(cart && cart.item_count);
    });
  }

  async function enforceFreeGiftEligibility(cart, blocks) {
    if (invalidGiftRemovalInFlight) return cart;

    const giftLines = getFreeGiftLines(cart);
    if (!giftLines.length) {
      syncCheckoutButtons(cart);
      return cart;
    }

    const giftThreshold = getEffectiveGiftThresholdCents(blocks);
    const belowThreshold = giftThreshold > 0 && getGiftEligibleTotal(cart) < giftThreshold;
    const giftOnly = isGiftOnlyCart(cart);
    if (!belowThreshold && !giftOnly) {
      syncCheckoutButtons(cart);
      return cart;
    }

    invalidGiftRemovalInFlight = true;
    try {
      closeAllRegionalModals(false);
      setDismissed(false);
      const payload = await removeFreeGiftLines(giftLines);
      if (payload) {
        await refreshCartDrawerAfterChange(payload);
      }
      try {
        const next = await fetchCart();
        syncCheckoutButtons(next);
        return next;
      } catch (_) {
        syncCheckoutButtons(cart);
        return cart;
      }
    } finally {
      invalidGiftRemovalInFlight = false;
    }
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error('Could not load cart');
    return res.json();
  }

  function getCartSectionId() {
    const drawer = getCartDrawer();
    return drawer
      ? drawer.getAttribute('data-section-id') || drawer.dataset.sectionId || 'cart-drawer'
      : 'cart-drawer';
  }

  async function refreshCartDrawerAfterChange(payload) {
    const api = window.MiniCartUpsellPDP;
    if (api && typeof api.syncHeaderCartCounts === 'function') {
      api.syncHeaderCartCounts(null, { data: payload || null });
    }
    if (api && typeof api.refreshCartAfterAdd === 'function') {
      await api.refreshCartAfterAdd(payload || {});
      return;
    }
    if (api && typeof api.fetchAndRefreshCartDrawer === 'function') {
      await api.fetchAndRefreshCartDrawer();
      return;
    }
    window.dispatchEvent(new CustomEvent('cart:updated', { detail: payload || null }));
  }

  async function removeFreeGiftLines(lines) {
    if (!lines || !lines.length) return null;
    const sectionId = getCartSectionId();
    const updates = {};
    lines.forEach((line) => {
      if (line && line.key) updates[line.key] = 0;
    });
    if (!Object.keys(updates).length) return null;

    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        updates,
        sections: sectionId,
        sections_url: window.location.pathname,
      }),
    });
    if (!res.ok) {
      let text = 'Could not update cart';
      try {
        const j = await res.json();
        if (j && j.description) text = j.description;
      } catch (_) {}
      throw new Error(text);
    }
    try {
      return await res.json();
    } catch (_) {
      return null;
    }
  }

  function isCartChangeRequestUrl(url) {
    try {
      const pathname = new URL(String(url || ''), window.location.origin).pathname;
      return /\/cart\/change(\.js)?$/.test(pathname);
    } catch (_) {
      return /\/cart\/change(\.js)?(\?|$)/.test(String(url || ''));
    }
  }

  function parseCartChangeBody(init) {
    if (!init || init.body == null) return null;
    const body = init.body;
    if (typeof body === 'string') {
      try {
        return JSON.parse(body);
      } catch (_) {
        try {
          const params = new URLSearchParams(body);
          const out = {};
          params.forEach((value, key) => {
            out[key] = value;
          });
          return out;
        } catch (_) {
          return null;
        }
      }
    }
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      const out = {};
      body.forEach((value, key) => {
        out[key] = value;
      });
      return out;
    }
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      const out = {};
      body.forEach((value, key) => {
        out[key] = value;
      });
      return out;
    }
    return null;
  }

  function findCartLineForChange(cart, body) {
    if (!cart || !Array.isArray(cart.items) || !body) return null;
    if (body.id != null && body.id !== '') {
      const id = String(body.id);
      return (
        cart.items.find((item) => String(item.key) === id) ||
        cart.items.find((item) => String(item.variant_id) === id) ||
        cart.items.find((item) => String(item.id) === id) ||
        null
      );
    }
    if (body.line != null && body.line !== '') {
      const idx = Number(body.line) - 1;
      if (idx >= 0 && idx < cart.items.length) return cart.items[idx];
    }
    return null;
  }

  function projectedPaidCentsAfterChange(cart, changedLine, newQty) {
    return cart.items.reduce((sum, item) => {
      if (isFreeGiftLine(item)) return sum;
      if (changedLine && item.key === changedLine.key) {
        if (newQty <= 0) return sum;
        const qty = Number(item.quantity) || 1;
        const unit = (Number(item.final_line_price) || 0) / qty;
        return sum + Math.round(unit * newQty);
      }
      return sum + (Number(item.final_line_price) || 0);
    }, 0);
  }

  function getCartUpdateUrl() {
    const routes = window.routes || (window.theme && window.theme.routes);
    if (routes && routes.cart_update_url) {
      const base = String(routes.cart_update_url);
      return /\.js(\?|$)/.test(base) ? base : base.replace(/\/?(\?|$)/, '.js$1');
    }
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/?$/, '/') + 'cart/update.js';
  }

  /**
   * Qty/remove on paid lines can leave gift + below-threshold (or gift-only).
   * Server validation rejects that intermediate cart/change, so rewrite to a
   * single cart/update that zeros gifts together with the paid-line change.
   */
  async function guardedCartChangeFetch(input, init, nativeFetch) {
    if (giftCartChangeGuardPassThrough || invalidGiftRemovalInFlight) {
      return nativeFetch(input, init);
    }

    const body = parseCartChangeBody(init);
    if (!body || body.quantity == null || body.quantity === '') {
      return nativeFetch(input, init);
    }

    let cart;
    try {
      cart = await fetchCart();
    } catch (_) {
      return nativeFetch(input, init);
    }

    const giftLines = getFreeGiftLines(cart);
    if (!giftLines.length) return nativeFetch(input, init);

    const line = findCartLineForChange(cart, body);
    if (!line || isFreeGiftLine(line)) return nativeFetch(input, init);

    const newQty = Number(body.quantity);
    if (!Number.isFinite(newQty) || newQty < 0) return nativeFetch(input, init);

    const paidAfter = projectedPaidCentsAfterChange(cart, line, newQty);
    const blocks = document.querySelectorAll(SELECTORS.threshold);
    const threshold = getEffectiveGiftThresholdCents(blocks);
    const wouldBeGiftOnly = paidAfter <= 0;
    const wouldBeBelowThreshold = threshold > 0 && paidAfter < threshold;

    if (!wouldBeGiftOnly && !wouldBeBelowThreshold) {
      return nativeFetch(input, init);
    }

    const updates = {};
    cart.items.forEach((item) => {
      if (isFreeGiftLine(item)) {
        updates[item.key] = 0;
      } else if (item.key === line.key) {
        updates[item.key] = newQty;
      } else {
        updates[item.key] = item.quantity;
      }
    });

    const updateBody = {
      updates,
    };
    if (body.sections != null) updateBody.sections = body.sections;
    if (body.sections_url != null) updateBody.sections_url = body.sections_url;

    const headers = Object.assign(
      {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      (init && init.headers) || {}
    );

    giftCartChangeGuardPassThrough = true;
    try {
      return await nativeFetch(getCartUpdateUrl(), {
        method: 'POST',
        headers: headers,
        credentials: (init && init.credentials) || 'same-origin',
        body: JSON.stringify(updateBody),
      });
    } finally {
      giftCartChangeGuardPassThrough = false;
    }
  }

  (function installGiftCartChangeGuard() {
    if (window.__jazzGiftCartChangeGuardInstalled) return;
    window.__jazzGiftCartChangeGuardInstalled = true;
    const nativeFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      try {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        const method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
        if (method === 'POST' && isCartChangeRequestUrl(url)) {
          return guardedCartChangeFetch(input, init || {}, nativeFetch);
        }
      } catch (err) {
        console.error('[mini-cart-gifts] cart change guard failed', err);
      }
      return nativeFetch(input, init);
    };
  })();

  const GIFT_ADD_DEFAULT_LABEL = 'Add';

  function rememberGiftAddButtonLabel(btn) {
    if (!btn || btn.hasAttribute('data-gift-add-label')) return;
    const label = (btn.textContent || '').trim();
    if (label && label !== 'Adding…') {
      btn.setAttribute('data-gift-add-label', label);
    }
  }

  function resetGiftAddButton(btn) {
    if (!btn) return;
    const label = btn.getAttribute('data-gift-add-label') || GIFT_ADD_DEFAULT_LABEL;
    btn.textContent = label;
    btn.removeAttribute('aria-busy');
    const unavailable = btn.getAttribute('data-variant-unavailable') === 'true';
    btn.disabled = unavailable;
  }

  function resetAllGiftAddButtons() {
    if (giftAddInFlight > 0) return;
    const drawer = getCartDrawer();
    if (!drawer) return;
    const selector = [SELECTORS.add, SELECTORS.regionalItemAdd].join(', ');
    drawer.querySelectorAll(selector).forEach(resetGiftAddButton);
  }

  function setGiftAddButtonsLoading(clickedBtn, loading) {
    const drawer = getCartDrawer();
    if (!drawer) return;
    const selector = [SELECTORS.add, SELECTORS.regionalItemAdd].join(', ');
    drawer.querySelectorAll(selector).forEach((btn) => {
      if (loading) {
        rememberGiftAddButtonLabel(btn);
        btn.disabled = true;
        if (clickedBtn && btn === clickedBtn) {
          btn.setAttribute('aria-busy', 'true');
          btn.textContent = 'Adding…';
        }
        return;
      }
      resetGiftAddButton(btn);
    });
  }

  function isCartDrawerVisible() {
    const d = getCartDrawer();
    if (!d) return false;
    if (d.hasAttribute('inert')) return false;
    if (d.getAttribute('aria-hidden') === 'true') return false;
    const st = window.getComputedStyle(d);
    if (st.display === 'none' || st.visibility === 'hidden' || st.opacity === '0') return false;
    return true;
  }

  function getModalInDrawer() {
    const d = getCartDrawer();
    return d ? d.querySelector(SELECTORS.modal) : null;
  }

  function getIntroModal() {
    const d = getCartDrawer();
    return d ? d.querySelector(SELECTORS.regionalIntro) : null;
  }

  function getPickerModal() {
    const d = getCartDrawer();
    return d ? d.querySelector(SELECTORS.regionalPicker) : null;
  }

  function isAnyRegionalModalOpen() {
    const intro = getIntroModal();
    const picker = getPickerModal();
    return (
      (intro && intro.getAttribute('data-open') === 'true') ||
      (picker && picker.getAttribute('data-open') === 'true')
    );
  }

  /** Move gift modals to drawer body so position:absolute covers only the mini cart. */
  function reparentGiftModalToDrawer() {
    if (!isFreeGiftRegion()) return;
    const drawer = getCartDrawer();
    if (!drawer) return;
    const body = drawer.querySelector('[data-sidebar-body]');
    if (!body) return;

    const selector = isRegionalFreeGiftFlow()
      ? [SELECTORS.modal, SELECTORS.regionalIntro, SELECTORS.regionalPicker].join(', ')
      : SELECTORS.modal;

    drawer.querySelectorAll(selector).forEach((m) => {
      if (m.parentNode !== body) body.appendChild(m);
    });
  }

  function getDismissed() {
    try {
      return sessionStorage.getItem(SESSION_DISMISS) === '1';
    } catch (_) {
      return false;
    }
  }

  function setDismissed(on) {
    try {
      if (on) sessionStorage.setItem(SESSION_DISMISS, '1');
      else sessionStorage.removeItem(SESSION_DISMISS);
    } catch (_) {}
  }

  function prefersReducedGiftMotion() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (_) {
      return false;
    }
  }

  function finishGiftModalClose(modal) {
    if (!modal || modal.getAttribute('data-open') === 'true') return;
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
  }

  function setModalOpen(modal, open) {
    if (!modal) return;

    if (modal._giftModalCloseTimer) {
      clearTimeout(modal._giftModalCloseTimer);
      modal._giftModalCloseTimer = null;
    }

    if (open) {
      modal.hidden = false;
      modal.removeAttribute('aria-hidden');
      modal.removeAttribute('data-open');

      if (prefersReducedGiftMotion()) {
        modal.setAttribute('data-open', 'true');
        return;
      }

      void modal.offsetHeight;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          modal.setAttribute('data-open', 'true');
        });
      });
      return;
    }

    modal.removeAttribute('data-open');

    if (prefersReducedGiftMotion()) {
      finishGiftModalClose(modal);
      return;
    }

    const panel = modal.querySelector('.cart-threshold__gift-modal-panel');
    const onTransitionEnd = (event) => {
      if (event.target !== panel || event.propertyName !== 'transform') return;
      panel.removeEventListener('transitionend', onTransitionEnd);
      finishGiftModalClose(modal);
    };

    if (panel) {
      panel.addEventListener('transitionend', onTransitionEnd);
    }

    modal._giftModalCloseTimer = setTimeout(() => {
      modal._giftModalCloseTimer = null;
      if (panel) panel.removeEventListener('transitionend', onTransitionEnd);
      finishGiftModalClose(modal);
    }, GIFT_MODAL_ANIM_MS + 60);
  }

  function closeAllRegionalModals(dismiss) {
    setModalOpen(getIntroModal(), false);
    setModalOpen(getPickerModal(), false);
    if (dismiss) setDismissed(true);
  }

  function openRegionalPicker() {
    const intro = getIntroModal();
    const picker = getPickerModal();
    const introWasOpen = intro && intro.getAttribute('data-open') === 'true';
    setModalOpen(intro, false);
    if (introWasOpen && picker) {
      const delay = prefersReducedGiftMotion() ? 0 : GIFT_MODAL_ANIM_MS + 40;
      setTimeout(() => setModalOpen(picker, true), delay);
      return;
    }
    setModalOpen(picker, true);
  }

  function openRegionalIntro() {
    setModalOpen(getPickerModal(), false);
    setModalOpen(getIntroModal(), true);
  }

  function getVariantIdForSlot(slot) {
    const drawer = getCartDrawer();
    if (!drawer || !slot) return null;
    const field = drawer.querySelector(
      SELECTORS.regionalVariant + '[data-free-gift-variant-select="' + slot + '"]'
    );
    if (!field) return null;
    const val = field.value;
    return val ? String(val) : null;
  }

  function updateThresholdBlock(block, cart) {
    if (!block) return;

    const totalCents = Number(cart && cart.total_price) || 0;
    const shipThreshold = Number(block.getAttribute('data-ship-threshold-cents')) || 0;
    let giftThreshold = Number(block.getAttribute('data-gift-threshold-cents')) || 0;
    if (giftThreshold < shipThreshold) giftThreshold = shipThreshold;
    const limitOne = block.getAttribute('data-limit-one-gift') !== 'false';
    const hasFreeGift = cartHasFreeGift(cart);

    const beforeText = block.getAttribute('data-text-before') || 'Add [[amount]] more to get Free Shipping on this order';
    const afterText =
      block.getAttribute('data-text-after') ||
      "You've unlocked free shipping! You're only [[amount]] away from receiving a FREE GIFT";
    const unlockedText =
      block.getAttribute('data-text-unlocked') ||
      "Congratulations! You've unlocked free shipping and free gift with your order.";
    const giftAddedText =
      block.getAttribute('data-text-gift-added') ||
      "Congratulations! You've unlocked free shipping and free gift with your order.";

    const fillPct = computeFillPct(totalCents, shipThreshold, giftThreshold);
    const drawer = getCartDrawer();
    const fill = block.querySelector('[data-cart-threshold-fill]');
    const bar = block.querySelector('[data-cart-threshold-bar]');
    const message = block.querySelector('[data-cart-threshold-message]');
    const mid = block.querySelector('[data-cart-threshold-mid]');
    const end = block.querySelector('[data-cart-threshold-end]');

    if (fill) fill.style.width = fillPct + '%';
    if (bar) bar.setAttribute('aria-valuenow', String(fillPct));
    if (mid) mid.classList.toggle('is-reached', totalCents >= shipThreshold && shipThreshold > 0);
    if (end) end.classList.toggle('is-reached', totalCents >= giftThreshold && giftThreshold > 0);

    if (message) {
      if (giftThreshold > 0 && totalCents >= giftThreshold) {
        message.textContent = hasFreeGift ? giftAddedText : unlockedText;
      } else if (shipThreshold > 0 && totalCents >= shipThreshold) {
        const remainingToGift = Math.max(0, giftThreshold - totalCents);
        message.innerHTML = withAmount(afterText, formatMoney(remainingToGift));
      } else {
        const remainingToShip = Math.max(0, shipThreshold - totalCents);
        message.innerHTML = withAmount(beforeText, formatMoney(remainingToShip));
      }
    }

    if (drawer) {
      drawer.querySelectorAll('[data-cart-threshold-gift-btn]').forEach((btn) => {
        const showGiftCta = totalCents >= giftThreshold && !(hasFreeGift && limitOne);
        btn.hidden = !showGiftCta;
        btn.disabled = false;
      });
    }

    const modal = getModalInDrawer();
    if (modal) {
      const p = modal.querySelector(SELECTORS.progress);
      if (p) p.textContent = (hasFreeGift ? '1' : '0') + '/1';
      modal.querySelectorAll(SELECTORS.add).forEach((btn) => {
        if (hasFreeGift && limitOne) {
          btn.disabled = true;
        } else {
          btn.disabled = btn.getAttribute('data-variant-unavailable') === 'true';
        }
      });
    }

    if (isRegionalFreeGiftFlow() && drawer) {
      syncRegionalGiftAddButtons(cart);
    }

    if (giftThreshold > 0 && totalCents < giftThreshold) {
      setDismissed(false);
    }
  }

  function tryAutoOpenGiftModal(cart, prevTotal) {
    if (!isFreeGiftRegion()) return;

    const block = getThresholdBlock();
    if (!block) return;
    const giftThreshold = Number(block.getAttribute('data-gift-threshold-cents')) || 0;
    if (giftThreshold <= 0) return;

    const totalCents = Number(cart && cart.total_price) || 0;
    if (totalCents < giftThreshold) return;
    if (cartHasFreeGift(cart) || getDismissed()) return;
    if (!isCartDrawerVisible()) return;

    const crossedUp =
      prevTotal >= 0 && prevTotal < giftThreshold && totalCents >= giftThreshold;
    if (!crossedUp && !pendingOpenCart) return;
    if (pendingOpenCart) pendingOpenCart = false;

    if (hasOffersTabs()) {
      setCartDrawerTab('cart');
    }

    if (isRegionalFreeGiftFlow()) {
      const intro = getIntroModal();
      if (!intro || intro.getAttribute('data-open') === 'true' || isAnyRegionalModalOpen()) return;
      openRegionalIntro();
      return;
    }

    const modal = getModalInDrawer();
    if (!modal) return;
    if (modal.querySelector(SELECTORS.add) == null) return;
    if (modal.getAttribute('data-open') === 'true') return;
    setModalOpen(modal, true);
  }

  async function refreshThresholdBars(options) {
    try {
      let cart =
        options && options.cart
          ? options.cart
          : await fetchCart();
      const blocks = document.querySelectorAll(SELECTORS.threshold);
      cart = await enforceFreeGiftEligibility(cart, blocks);
      syncCheckoutButtons(cart);

      if (!isFreeGiftRegion() || !blocks.length) return;

      if (typeof window.MiniCartApplyMoneyConfigFromCart === 'function') {
        window.MiniCartApplyMoneyConfigFromCart(cart);
      }
      const totalCents = Number(cart.total_price) || 0;
      const prevTotal = lastCartTotalCents;

      reparentGiftModalToDrawer();
      blocks.forEach((block) => updateThresholdBlock(block, cart));
      updateOffersPanel(cart);
      tryAutoOpenGiftModal(cart, prevTotal);
      lastCartTotalCents = totalCents;
    } catch (err) {
      console.error('[mini-cart-gifts] threshold refresh failed', err);
    }
  }

  function closestModal(el) {
    return el ? el.closest(SELECTORS.modal) : null;
  }

  async function addGift(variantId) {
    const url = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
    const sectionId = getCartSectionId();
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: Number(variantId),
        quantity: 1,
        properties: { _jazz_free_gift: 'true' },
        sections: sectionId,
        sections_url: window.location.pathname,
      }),
    });

    if (!res.ok) {
      let text = 'Failed to add gift.';
      try {
        const j = await res.json();
        if (j && j.description) text = j.description;
      } catch (_) {}
      throw new Error(text);
    }

    let payload = null;
    try {
      payload = await res.json();
    } catch (_) {}

    setDismissed(false);
    await refreshCartDrawerAfterChange(payload);
    if (hasOffersTabs()) {
      setCartDrawerTab('cart');
    }
  }

  /** Only one free gift line allowed; queued so rapid clicks cannot add two. */
  async function addGiftSafely(variantId) {
    const vid = String(variantId);
    const cart = await fetchCart();
    const existing = getFreeGiftLines(cart);

    if (existing.some((line) => String(line.variant_id) === vid)) {
      return;
    }

    if (existing.length > 0) {
      const removePayload = await removeFreeGiftLines(existing);
      if (removePayload && removePayload.sections) {
        await refreshCartDrawerAfterChange(removePayload);
      }
    }

    await addGift(variantId);
  }

  function requestGiftAdd(variantId, clickedBtn) {
    if (!variantId) return giftAddQueue;
    if (giftAddInFlight > 0 || (clickedBtn && clickedBtn.getAttribute('aria-busy') === 'true')) {
      return giftAddQueue;
    }

    giftAddInFlight += 1;
    setGiftAddButtonsLoading(clickedBtn, true);

    giftAddQueue = giftAddQueue
      .then(() => addGiftSafely(variantId))
      .then(() => fetchCart())
      .then((cart) => refreshThresholdBars({ cart }))
      .catch((err) => {
        console.error('[mini-cart-gifts]', err);
      })
      .finally(() => {
        giftAddInFlight = Math.max(0, giftAddInFlight - 1);
        setGiftAddButtonsLoading(null, false);
        fetchCart()
          .then((cart) => refreshThresholdBars({ cart }))
          .catch(() => refreshThresholdBars());
      });

    return giftAddQueue;
  }

  function onClick(e) {
    const offersTabBtn = e.target.closest(SELECTORS.offersTab);
    if (offersTabBtn && hasOffersTabs()) {
      setCartDrawerTab(offersTabBtn.getAttribute('data-cart-drawer-tab'));
      return;
    }

    const showOffersProducts = e.target.closest(SELECTORS.offersShowProducts);
    if (showOffersProducts) {
      openRegionalPicker();
      return;
    }

    if (e.target.closest(SELECTORS.regionalOpenPicker)) {
      openRegionalPicker();
      return;
    }

    if (e.target.closest(SELECTORS.regionalIntroAdd)) {
      openRegionalPicker();
      return;
    }

    if (e.target.closest(SELECTORS.regionalIntroPass)) {
      closeAllRegionalModals(true);
      return;
    }

    const regionalClose = e.target.closest(SELECTORS.regionalClose);
    if (regionalClose) {
      closeAllRegionalModals(true);
      return;
    }

    const regionalOverlay = e.target.closest(SELECTORS.regionalOverlay);
    if (regionalOverlay) {
      closeAllRegionalModals(true);
      return;
    }

    const regionalAdd = e.target.closest(SELECTORS.regionalItemAdd);
    if (regionalAdd) {
      e.preventDefault();
      const slot = regionalAdd.getAttribute('data-free-gift-slot');
      const variantId = getVariantIdForSlot(slot);
      if (!variantId || regionalAdd.disabled) return;
      requestGiftAdd(variantId, regionalAdd).then(() => {
        closeAllRegionalModals(false);
      });
      return;
    }

    const open = e.target.closest(SELECTORS.openBtn);
    if (open) {
      const drawer = e.target.closest('#CartDrawer');
      const modal = drawer && drawer.querySelector(SELECTORS.modal);
      if (modal) setModalOpen(modal, true);
      return;
    }

    const close = e.target.closest(SELECTORS.close);
    if (close) {
      const m = closestModal(close);
      setModalOpen(m, false);
      if (m) setDismissed(true);
      return;
    }

    const add = e.target.closest(SELECTORS.add);
    if (add) {
      e.preventDefault();
      const variantId = add.getAttribute('data-variant-id');
      if (!variantId || add.disabled) return;
      requestGiftAdd(variantId, add).then(() => {
        const modal = closestModal(add);
        setModalOpen(modal, false);
      });
    }
  }

  function onKeydown(e) {
    if (e.key !== 'Escape') return;
    const drawer = getCartDrawer();
    if (!drawer) return;

    if (isRegionalFreeGiftFlow() && isAnyRegionalModalOpen()) {
      closeAllRegionalModals(true);
      return;
    }

    drawer.querySelectorAll(SELECTORS.modal).forEach((m) => {
      if (m.getAttribute('data-open') === 'true') {
        setModalOpen(m, false);
        setDismissed(true);
      }
    });
  }

  let thresholdTimer = null;
  function scheduleThresholdRefresh(delay) {
    clearTimeout(thresholdTimer);
    thresholdTimer = setTimeout(() => {
      if (giftAddInFlight > 0) return;
      refreshThresholdBars();
    }, delay || 220);
  }

  document.addEventListener('click', onClick);
  document.addEventListener('keydown', onKeydown);

  async function validateCheckoutOrBlock(event) {
    const checkoutBtn = event.target.closest(
      '[data-cart-checkout-button], [data-cart-page-checkout-button]'
    );
    if (!checkoutBtn) return false;
    if (checkoutBtn.disabled || checkoutBtn.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
      window.alert(GIFT_ONLY_CHECKOUT_MESSAGE);
      return true;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    let cart = null;
    try {
      const blocks = document.querySelectorAll(SELECTORS.threshold);
      cart = await fetchCart();
      cart = await enforceFreeGiftEligibility(cart, blocks);
      syncCheckoutButtons(cart);
      await refreshThresholdBars({ cart });
    } catch (err) {
      console.error('[mini-cart-gifts] checkout gift validation failed', err);
    }

    if (shouldBlockCheckout(cart)) {
      window.alert(GIFT_ONLY_CHECKOUT_MESSAGE);
      return true;
    }

    if (checkoutBtn.matches('[data-cart-page-checkout-button]')) {
      const form = checkoutBtn.form || document.getElementById('CartPageForm');
      if (form) form.submit();
      return true;
    }

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const href =
      checkoutBtn.getAttribute('href') && checkoutBtn.getAttribute('href') !== '#'
        ? checkoutBtn.getAttribute('href')
        : root.replace(/\/?$/, '/') + 'checkout';
    window.location.href = href;
    return true;
  }

  document.addEventListener(
    'click',
    (event) => {
      const maybeCheckout = event.target.closest(
        '[data-cart-checkout-button], [data-cart-page-checkout-button]'
      );
      if (!maybeCheckout) return;
      validateCheckoutOrBlock(event);
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!form || form.id !== 'CartPageForm') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      (async () => {
        let cart = null;
        try {
          const blocks = document.querySelectorAll(SELECTORS.threshold);
          cart = await fetchCart();
          cart = await enforceFreeGiftEligibility(cart, blocks);
          syncCheckoutButtons(cart);
        } catch (err) {
          console.error('[mini-cart-gifts] cart submit gift validation failed', err);
        }
        if (shouldBlockCheckout(cart)) {
          window.alert(GIFT_ONLY_CHECKOUT_MESSAGE);
          return;
        }
        form.submit();
      })();
    },
    true
  );
  document.addEventListener('click', (e) => {
    if (
      e.target.closest('quantity-btn') ||
      e.target.closest('cart-remove-button') ||
      e.target.closest('.cart-item__remove-button')
    ) {
      scheduleThresholdRefresh(520);
    }
  });
  function applyFreeGiftVariantPreview(select) {
    if (!select || select.tagName !== 'SELECT') return;
    const item = select.closest('[data-free-gift-item]');
    if (!item) return;
    const option = select.options[select.selectedIndex];
    if (!option) return;

    const img = item.querySelector('.mcu-item__image');
    if (img) {
      const src = option.getAttribute('data-variant-image');
      const alt = option.getAttribute('data-variant-image-alt');
      if (src) img.src = src;
      if (alt) img.alt = alt;
    }

    const compareCents = Number(option.getAttribute('data-variant-compare-cents')) || 0;
    let compare = item.querySelector('.mcu-item__compare');
    if (compareCents > 0) {
      if (!compare) {
        const priceRow = item.querySelector('.mcu-item__price-row');
        if (priceRow) {
          compare = document.createElement('span');
          compare.className = 'mcu-item__compare';
          priceRow.appendChild(compare);
        }
      }
      if (compare) {
        compare.textContent = formatMoney(compareCents);
        compare.hidden = false;
      }
    } else if (compare) {
      compare.hidden = true;
    }
  }

  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-free-gift-variant-select]')) {
      applyFreeGiftVariantPreview(e.target);
      return;
    }
    if (e.target.matches('[data-quantity-input], .cart-item-quantity__input')) {
      scheduleThresholdRefresh(520);
    }
  });
  window.addEventListener('cart:updated', () => scheduleThresholdRefresh(320));
  window.addEventListener('cart:refresh', () => scheduleThresholdRefresh(380));

  const cartDrawer = getCartDrawer();
  if (cartDrawer && typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(() => scheduleThresholdRefresh(220));
    observer.observe(cartDrawer, { childList: true, subtree: true, attributes: true });
  }
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-sidebar-id="CartDrawer"], [data-sidebar-button]')) {
      pendingOpenCart = true;
      setTimeout(function () {
        pendingOpenCart = false;
      }, 2000);
      scheduleThresholdRefresh(280);
    } else if (e.target.closest('a[href="/cart"]')) {
      scheduleThresholdRefresh(280);
    }
  });

  reparentGiftModalToDrawer();
  scheduleThresholdRefresh(80);

  window.MiniCartEnforceFreeGifts = function () {
    return refreshThresholdBars();
  };
})();
